import { supabase } from './supabase';
import { logger } from './logger';
import { aiResourceGenerator } from './ai-resource-generator';

export interface CoachingResource {
  id: string;
  title: string;
  url: string;
  description: string;
  category_id: string;
  subcategory: string;
  trade_type: string;
  keywords: string[];
  quality_score: number;
  is_verified: boolean;
  coaching_topic_id?: string;
}

export interface ResourceCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  color_scheme: string;
}

class ResourceMatchingService {
  private static instance: ResourceMatchingService;
  private cachedResources: CoachingResource[] | null = null;
  private cachedCategories: ResourceCategory[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000;

  private constructor() {}

  public static getInstance(): ResourceMatchingService {
    if (!ResourceMatchingService.instance) {
      ResourceMatchingService.instance = new ResourceMatchingService();
    }
    return ResourceMatchingService.instance;
  }

  private async loadResources(): Promise<void> {
    const now = Date.now();
    if (this.cachedResources && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      return;
    }

    try {
      const { data: resources, error: resourceError } = await supabase
        .from('coaching_resources')
        .select('*')
        .eq('is_active', true)
        .order('quality_score', { ascending: false });

      if (resourceError) throw resourceError;

      const { data: categories, error: categoryError } = await supabase
        .from('resource_categories')
        .select('*')
        .eq('is_active', true);

      if (categoryError) throw categoryError;

      this.cachedResources = resources || [];
      this.cachedCategories = categories || [];
      this.cacheTimestamp = now;
    } catch (error) {
      logger.error('Error loading resources:', error);
      this.cachedResources = [];
      this.cachedCategories = [];
    }
  }

  private getDistractionKeywords(): string[] {
    return [
      'gaming', 'gamer', 'game', 'esports', 'streamer', 'streaming', 'twitch', 'youtube',
      'content creator', 'influencer', 'tiktok', 'social media', 'instagram',
      'game tester', 'qa tester', 'game developer', 'game design', 'pro gamer',
      'youtuber', 'content creation', 'video editing', 'streaming platform'
    ];
  }

  private filterDistractionResources(resources: CoachingResource[], userDistractions: string[] = []): CoachingResource[] {
    if (userDistractions.length === 0) return resources;

    const distractionKeywords = this.getDistractionKeywords();
    const userDistractionLower = userDistractions.map(d => d.toLowerCase());

    return resources.filter(resource => {
      const resourceText = `${resource.title} ${resource.description} ${resource.subcategory} ${resource.keywords.join(' ')}`.toLowerCase();

      // Check if resource contains any distraction-related keywords
      const hasDistractionKeyword = distractionKeywords.some(keyword => resourceText.includes(keyword));
      if (hasDistractionKeyword) {
        logger.info(`Filtering out resource "${resource.title}" - contains distraction keywords`);
        return false;
      }

      // Check if resource relates to user's specific distractions
      const relatedToUserDistraction = userDistractionLower.some(distraction =>
        resourceText.includes(distraction) || distraction.includes('gaming') && resourceText.includes('game')
      );
      if (relatedToUserDistraction) {
        logger.info(`Filtering out resource "${resource.title}" - related to user distraction: ${userDistractions.join(', ')}`);
        return false;
      }

      return true;
    });
  }

  public async matchResources(
    messageText: string,
    conversationHistory: Array<{ role: string; content: string }> = [],
    topicId?: string,
    limit: number = 3,
    userDistractions: string[] = [],
    topicCategory?: string
  ): Promise<CoachingResource[]> {
    try {
      // First, try to generate AI resources based on conversation context
      logger.info('Attempting to generate AI resources for conversation');

      const aiResources = await aiResourceGenerator.generateResources({
        messageText,
        conversationHistory,
        topicId,
        limit: limit * 2, // Get more to account for filtering
        topicCategory
      });

      // If we got AI-generated resources, filter and use them
      if (aiResources && aiResources.length > 0) {
        logger.info(`Generated ${aiResources.length} AI resources, now filtering for distractions`);

        // Convert GeneratedResource to CoachingResource format
        const convertedResources = aiResources.map(resource => ({
          ...resource,
          category_id: 'ai-generated',
          trade_type: resource.trade_type || ''
        }));

        // Filter out distraction-related resources
        const filteredResources = this.filterDistractionResources(convertedResources, userDistractions);
        logger.info(`${filteredResources.length} resources remaining after filtering`);

        return filteredResources.slice(0, limit);
      }

      // Fallback to static database resources if AI generation fails or returns nothing
      logger.info('Falling back to static database resources');
      await this.loadResources();

      if (!this.cachedResources || this.cachedResources.length === 0) {
        return [];
      }

      const contextText = this.buildContextText(messageText, conversationHistory);
      const keywords = this.extractKeywords(contextText);

      // Filter resources by topic if available
      let availableResources = this.cachedResources;
      if (topicId) {
        // Prioritize resources associated with the current topic
        const topicResources = availableResources.filter(r => r.coaching_topic_id === topicId);

        // Get category mappings for this topic
        const categoryMappings = await this.getTopicCategoryMappings(topicId);
        const priorityCategories = categoryMappings.map(m => m.resource_category_id);

        // Include resources from priority categories
        const categoryResources = availableResources.filter(r =>
          priorityCategories.includes(r.category_id)
        );

        // Combine and deduplicate
        const combinedResources = [...topicResources, ...categoryResources];
        const uniqueResources = Array.from(new Map(combinedResources.map(r => [r.id, r])).values());

        if (uniqueResources.length > 0) {
          availableResources = uniqueResources;
        }
      }

      const scoredResources = availableResources.map(resource => {
        const score = this.calculateRelevanceScore(keywords, resource, topicCategory);
        return { resource, score };
      });

      scoredResources.sort((a, b) => {
        if (b.score === a.score) {
          return b.resource.quality_score - a.resource.quality_score;
        }
        return b.score - a.score;
      });

      const relevantResources = scoredResources
        .filter(item => item.score > 0)
        .map(item => item.resource);

      // Filter out distraction-related resources
      const filteredResources = this.filterDistractionResources(relevantResources, userDistractions);

      return filteredResources.slice(0, limit);
    } catch (error) {
      logger.error('Error in matchResources:', error);
      return [];
    }
  }

  private buildContextText(messageText: string, conversationHistory: Array<{ role: string; content: string }>): string {
    const recentMessages = conversationHistory.slice(-5);
    const contextParts = recentMessages.map(msg => msg.content);
    contextParts.push(messageText);
    return contextParts.join(' ').toLowerCase();
  }

  private extractKeywords(text: string): string[] {
    const lowerText = text.toLowerCase();

    const tradeKeywords = [
      'apprenticeship', 'apprentice', 'trade', 'trades', 'tradesman', 'skilled',
      'electrician', 'electrical', 'electric', 'wiring',
      'plumber', 'plumbing', 'pipe', 'pipefitter',
      'hvac', 'heating', 'cooling', 'air conditioning',
      'carpenter', 'carpentry', 'woodwork', 'framing',
      'welder', 'welding', 'fabrication',
      'mechanic', 'automotive', 'diesel',
      'ironworker', 'steel', 'structural',
      'mason', 'masonry', 'brick', 'concrete',
      'roofer', 'roofing',
      'construction', 'building', 'contractor',
      'union', 'local', 'hall',
      'certification', 'license', 'credential',
      'training', 'school', 'program', 'course',
      'job', 'career', 'work', 'employment', 'hiring',
      'salary', 'pay', 'wage', 'income',
      'tools', 'equipment', 'safety',
      'osha', 'epa',
      'community', 'forum', 'advice'
    ];

    const foundKeywords = tradeKeywords.filter(keyword => lowerText.includes(keyword));
    return foundKeywords;
  }

  private calculateRelevanceScore(keywords: string[], resource: CoachingResource, topicCategory?: string): number {
    let score = 0;

    keywords.forEach(keyword => {
      if (resource.keywords.some(rk => rk.toLowerCase().includes(keyword) || keyword.includes(rk.toLowerCase()))) {
        score += 3;
      }

      const searchText = `${resource.title} ${resource.description} ${resource.subcategory} ${resource.trade_type}`.toLowerCase();
      if (searchText.includes(keyword)) {
        score += 2;
      }
    });

    // Boost score for category-specific resources
    if (topicCategory) {
      const categoryKeywords = this.getCategoryKeywords(topicCategory);
      const resourceText = `${resource.title} ${resource.description} ${resource.subcategory} ${resource.keywords.join(' ')}`.toLowerCase();

      categoryKeywords.forEach(catKeyword => {
        if (resourceText.includes(catKeyword)) {
          score += 4;
        }
      });
    }

    score += resource.quality_score * 0.5;

    if (resource.is_verified) {
      score += 1;
    }

    return score;
  }

  private getCategoryKeywords(category: string): string[] {
    const categoryKeywordsMap: Record<string, string[]> = {
      habits: ['habit', 'routine', 'behavior', 'change', 'accountability', 'discipline', 'consistency', 'addiction', 'breaking', 'building'],
      career: ['career', 'job', 'work', 'employment', 'profession', 'trade', 'apprentice', 'skill', 'training'],
      health: ['health', 'fitness', 'workout', 'exercise', 'nutrition', 'wellness', 'mental', 'physical'],
      relationships: ['relationship', 'dating', 'communication', 'connection', 'social', 'family', 'partner'],
      mindset: ['mindset', 'mental', 'psychology', 'thinking', 'belief', 'motivation', 'confidence'],
      personal: ['personal', 'growth', 'development', 'self-improvement', 'goals', 'planning'],
      dating: ['dating', 'relationship', 'attraction', 'communication', 'social skills'],
      finance: ['finance', 'money', 'budget', 'saving', 'investing', 'debt', 'income']
    };

    return categoryKeywordsMap[category.toLowerCase()] || [];
  }

  private async getTopicCategoryMappings(topicId: string): Promise<Array<{ resource_category_id: string; priority_weight: number }>> {
    try {
      const { data, error } = await supabase
        .from('coaching_topic_resource_categories')
        .select('resource_category_id, priority_weight')
        .eq('coaching_topic_id', topicId)
        .order('priority_weight', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error fetching topic category mappings:', error);
      return [];
    }
  }

  public async trackInteraction(
    userId: string,
    resourceId: string,
    interactionType: 'click' | 'save' | 'helpful' | 'not_helpful',
    rating?: number
  ): Promise<void> {
    try {
      await supabase
        .from('user_resource_interactions')
        .insert({
          user_id: userId,
          resource_id: resourceId,
          interaction_type: interactionType,
          rating: rating
        });
    } catch (error) {
      logger.error('Error tracking resource interaction:', error);
    }
  }

  public async getSavedResources(userId: string): Promise<CoachingResource[]> {
    try {
      const { data, error } = await supabase
        .from('user_resource_interactions')
        .select('resource_id, coaching_resources(*)')
        .eq('user_id', userId)
        .eq('interaction_type', 'save')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || [])
        .map((item: any) => item.coaching_resources)
        .filter(Boolean);
    } catch (error) {
      logger.error('Error getting saved resources:', error);
      return [];
    }
  }

  public async getResourcesByCategory(categoryName: string): Promise<CoachingResource[]> {
    try {
      const { data: category } = await supabase
        .from('resource_categories')
        .select('id')
        .eq('name', categoryName)
        .maybeSingle();

      if (!category) return [];

      const { data, error } = await supabase
        .from('coaching_resources')
        .select('*')
        .eq('category_id', category.id)
        .eq('is_active', true)
        .order('quality_score', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error getting resources by category:', error);
      return [];
    }
  }

  public async getAllCategories(): Promise<ResourceCategory[]> {
    await this.loadResources();
    return this.cachedCategories || [];
  }
}

export const resourceMatching = ResourceMatchingService.getInstance();
